import { useMutation } from '@src/query';

import { listingsKeyPrefix } from '../listings/useListingMutations';
import { partsKeyPrefix } from './usePartFormMutations';


/**
 * M26 listing-health mutation hooks (Task 5). Wraps the shared `useMutation`
 * engine with the injected-`queryClient` `mutateFn({ apiClient, queryClient })`
 * contract — the corelib query singleton is NOT imported here. Invalidation runs
 * inside `mutateFn` (the one place `queryClient` is injected).
 *
 * Mirrors `query/listings/useListingMutations.js`: one
 * `query/<area>/use<Thing>Mutations.js` module re-exported through the
 * `@src/query` barrel.
 */

/** Cache key prefix for a seller's listing-health report (drawer items + the
 * dashboard `?summary=1` card). A price edit changes a row's market delta, so
 * the report is invalidated alongside the tracker + inventory lists. */
function listingHealthKeyPrefix(sellerId) {
  return ['root', '', 'inventory', 'listing-health', String(sellerId || '')];
}


/**
 * Advisory price confirm from the health drawer (Plane #440). Variables:
 * `{ sellerId, partId, price }`. PATCHes the existing part endpoint with the
 * user-confirmed `{ price }` (the analyzer only ever PREFILLS the suggestion —
 * this fires solely on the explicit Save click), then invalidates the tracker,
 * inventory, and health caches so every view reflects the new price.
 */
function usePartPriceMutation(params) {
  return useMutation({
    mutateFn: async ({ apiClient, queryClient, sellerId, partId, price }) => {
      const data = await apiClient.request({
        url: `/inventory/parts/${sellerId}/${partId}/`,
        method: 'PATCH',
        payload: { price },
      });
      queryClient.invalidateQueries({ queryKey: listingsKeyPrefix(sellerId) });
      queryClient.invalidateQueries({ queryKey: partsKeyPrefix(sellerId) });
      queryClient.invalidateQueries({ queryKey: listingHealthKeyPrefix(sellerId) });
      return data;
    },
    ...params,
  });
}


export {
  listingHealthKeyPrefix,
  usePartPriceMutation,
};
